import { Component } from '@angular/core';
import { Project } from '../masterdata/models/project';
import { MasterDataService } from '../masterdata/masterdata.service';
import { MessagesNewService } from '../messages-new/messages.service';
import { ProjectWorkflow } from '../masterdata/models/projectWorkflow';
import { ErrorHandler } from '../utils/error-handler';
import { BoxDeliveryDTO } from '../models/boxDelivery';
import { BoxManagementService } from '../box-management/box-management.service';
import { MachineNewService } from './machines-new.service';
import { ProcessStep } from '../models-new/processStep';
import { RunProcessStep } from '../models/run-steps';
import { PlateProcessingService } from '../plate-processing/plate-processing.service';
import { RunStatus } from '../constants/Status';
import { Run } from '../models-new/run';

@Component({
	selector: 'app-machines-new',
	templateUrl: './machines-new.component.html',
	styleUrls: ['./machines-new.component.css']
})
export class MachinesNewComponent {

	blocked = false;
	errorHandler: ErrorHandler = new ErrorHandler;
	plateTableSidebarVisible = false;
	availableRunStatuses: Array<any> = [];
	puid: string;
	selectedLabId: any;
	selectedProject: Project;
	projectsList = [];
	projectData: Project[] = [];
	projectsMap = new Map();
	workflowData: ProjectWorkflow[] = [];
	projectWorkflowMap: Map<string, ProjectWorkflow> = new Map();
	workflowList = [];
	selectedWorkflow: ProjectWorkflow;
	boxIDSearchValue = "";
	selectedRun: Run = new Run();
	linesList = [
		{ label: "Line_EGL", value: "Line_EGL" },
		{ label: "Line_TSU", value: "Line_TSU" }
	];
	runs: Run[] = []
	duplicatePlateID = "";
	showDuplicatePlateIdDialog = false;
	disableUpdatePlateIDButton = true;
	newPlateID: string;
	showRunComment = false;

	constructor(private readonly messagesService: MessagesNewService,
		private readonly boxManagementService: BoxManagementService,
		private readonly masterDataService: MasterDataService,
		private readonly machineNewService: MachineNewService,
		private readonly plateProcessingService: PlateProcessingService) { }

	ngOnInit() {
		this.puid = localStorage.getItem("PUID");
		this.selectedLabId = JSON.parse(localStorage.getItem('lab')).lab._id;
		this.availableRunStatuses.push(
			{ 'name': RunStatus.RUN_STARTED, 'value': RunStatus.RUN_STARTED },
			{ 'name': RunStatus.RUN_COMPLETED, 'value': RunStatus.RUN_COMPLETED }
		)
		this.getProjectFromMDM();
		this.getWorkflowsFromMDM();
	}

	getProjectFromMDM() {
		this.masterDataService.getConfigList(this.selectedLabId, "project", 0, 1000, true).subscribe({
			next: (results) => {
				this.projectData = results.data;
				this.projectsList = [];
				this.projectData.forEach(project => {
					this.projectsList.push({ label: project.projectName, value: project })
					this.projectsMap.set(project.projectId, project);
				})
			},
			error: (error) => {
				this.showError(error);
			}
		})
	}

	getWorkflowsFromMDM() {
		this.masterDataService.getConfigList(this.selectedLabId, "projectWorkflow", 0, 1000, true).subscribe({
			next: (results) => {
				this.workflowData = results.data;
				this.getWorkflowsForProject();
				this.workflowData.forEach(workflow => {
					this.projectWorkflowMap.set(`${workflow.projectId}_${workflow.workflowName}`, workflow);
				})
			},
			error: (error) => {
				this.showError(error);
			}
		})
	}

	getWorkflowsForProject() {
		this.workflowList = [];
		if (!this.selectedProject?.projectId) {
			return;
		}

		this.workflowData.forEach(workflow => {
			if (workflow.projectId === this.selectedProject?.projectId && workflow.automatedReception) {
				this.workflowList.push({ label: workflow.workflowName, value: workflow });
			}
		});
	}

	showError(error: any) {
		const message = this.errorHandler.handleError(error);
		this.messagesService.addErrorMessage(message);
	}

	searchForBox() {
		if (!this.selectedProject) {
			this.messagesService.addWarningMessage({
				severity: 'warn', summary: 'Project selection',
				detail: 'Please select a project id to proceed further'
			})
			return
		}
		if ("" === this.boxIDSearchValue.trim()) {
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "Box search failed!",
				detail: "Please a search with a valid boxID!"
			})
			return;
		}

		if (this.selectedRun?.boxes?.includes(this.boxIDSearchValue)) {
			this.blocked = false;
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "Box not added!",
				detail: `Box with boxId - ${this.boxIDSearchValue} is already added!`

			})
			this.boxIDSearchValue = "";
			return;
		}

		this.blocked = true;
		this.boxManagementService.validateBox(this.boxIDSearchValue, this.selectedLabId).subscribe({
			next: result => {
				this.blocked = false;
				const boxDetails: BoxDeliveryDTO = result.data;
				if (!boxDetails) {
					this.messagesService.addWarningMessage({
						severity: "warn", summary: "Box not found!",
						detail: `Box with boxId - ${this.boxIDSearchValue} not found!`
					})
					this.boxIDSearchValue = "";
					return;
				}

				if (boxDetails.status === "COMPLETED") {
					this.messagesService.addWarningMessage({
						severity: "warn", summary: "Box not eligible!",
						detail: "Box has wrong status - " + boxDetails.status
					})
					this.boxIDSearchValue = "";
					return;
				}

				if (boxDetails.projectId !== this.selectedProject.projectId) {
					this.messagesService.addWarningMessage({
						severity: "warn", summary: "Box not eligible!",
						detail: "Box belongs to a different project - " + boxDetails.projectId
					})
					this.boxIDSearchValue = "";
					return;
				}
				if (!this.selectedRun.boxes || this.selectedRun.boxes.length === 0) {
					this.selectedRun.boxes = [boxDetails.boxId]
					this.selectedRun.boxReceptionDate = boxDetails.receptionDate
					this.boxIDSearchValue = ''
					if (this.selectedRun.runId !== "") {
						this.updateRun("BoxId added to the run successfully!")
					}
					return
				}
				this.selectedRun.boxReceptionDate = new Date(boxDetails.receptionDate)
				this.selectedRun.boxes.push(boxDetails.boxId)
				this.boxIDSearchValue = '';
				if (this.selectedRun.runId !== "") {
					this.updateRun("BoxId added to the run successfully!")
				}
			},
			error: error => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity: 'error', summary: "Box fetch failed!!",
					detail: error.error
				})
			}
		});
	}

	updateRun(successMessage: string = null) {
		this.selectedRun.lastModifiedBy = this.puid;
		this.blocked = true;
		this.machineNewService.updateRun(this.selectedRun, this.selectedLabId).subscribe({
			next: _result => {
				this.messagesService.addSuccessMessage({
					severity: "success", summary: "Run updated successfully",
					detail: successMessage || "Run updated successfully"

				})
				this.blocked = false;
			},
			error: error => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity: "error", summary: "Update Run Failed!",
					detail: error.error
				})
			}
		})
	}

	removeRunSelection() {
		this.selectedRun = new Run();
		this.selectedRun.boxes = [];
		this.selectedRun.runId = "";
		this.selectedProject = null;
		this.selectedWorkflow = null;
	}


	disableRunFormFields() {
		return this.selectedRun.status === RunStatus.RUN_COMPLETED || this.selectedRun.status === RunStatus.RUN_STARTED;
	}

	deleteValue(index: number) {
		const boxesLength = this.selectedRun.boxes?.length;
		this.selectedRun.boxes.splice(index, 1);

		if (index === boxesLength - 1) {
			this.blocked = true;
			this.boxManagementService.validateBox(this.selectedRun.boxes[index - 1], this.selectedLabId).subscribe({
				next: result => {
					this.blocked = false;
					const boxDetails: BoxDeliveryDTO = result.data;
					this.selectedRun.boxReceptionDate = boxDetails.receptionDate;
				},
				error: error => {
					this.blocked = false;
					this.messagesService.addErrorMessage({
						severity: 'error', summary: "Box fetch failed!!",
						detail: error.error
					})
				}
			});
		}

		if (this.selectedRun.runId !== "") {
			this.updateRun("BoxId removed successfully!")
		}
	}

	startRun() {
		if (!this.checkForMandatoryFieldsForNewRun()) {
			return;
		}
		this.addRequiredFieldsForNewRun();
		this.blocked = true;
		this.machineNewService.createRun(this.selectedRun, this.selectedLabId).subscribe({
			next: result => {
				this.selectedRun = result.data;
				this.messagesService.addSuccessMessage({
					severity: "success", summary: "Run Started",
					detail: "New Run started with runID " + this.selectedRun.runId
				})
				this.blocked = false;
				this.fetchRuns();
			},
			error: error => {
				this.messagesService.addErrorMessage({
					severity: "error", summary: "Failed to start a new run",
					detail: error.error
				})
				this.blocked = false;
			}
		})
	}

	fetchRuns(workFlow?: string, fromDate?: Date) {
		this.blocked = true;
		if (workFlow === '') {
			workFlow = undefined;
		}
		if (fromDate === undefined) {
			fromDate = undefined;
		}
		this.machineNewService.fetchRuns(this.selectedLabId, workFlow, fromDate).subscribe({
			next: (result) => {
				this.runs = result.data;
				this.blocked = false;
			},
			error: (error) => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity: 'error', summary: 'Failed to fetch runs',
					detail: 'Some issue in pulling the runs info, please try again'
				})
			}
		})
	}

	setSampleType(){
		if (this.selectedRun.line === 'Line_TSU') {
			this.selectedRun.sampleType = "TSU";
		}
		else if (this.selectedRun.line === 'Line_EGL') {
			this.selectedRun.sampleType = "EGL";
		}else{
			this.selectedRun.sampleType = null;
		}
	}

	addRequiredFieldsForNewRun() {
		this.selectedRun.projectId = this.selectedProject.projectId;
		this.selectedRun.projectName = this.selectedProject.projectName;
		this.selectedRun.workFlow = this.selectedWorkflow.workflowName;
		this.selectedRun.proxyWeatherbyAPI = !this.selectedProject.updateWeatherbys;
		this.selectedRun.status = RunStatus.RUN_STARTED;
		const newStep = new ProcessStep();
		newStep.step = RunProcessStep.CREATE_RUN;
		newStep.startTimeStamp = new Date();
		newStep.startPUID = this.puid;
		newStep.endPUID = "";
		newStep.endTimeStamp = null;
		this.selectedRun.processSteps = [newStep]
		this.selectedRun.samples = [];
		this.selectedRun.plates = [];
		this.selectedRun.comments = [];
		this.selectedRun.machineId = "";
		this.selectedRun.lastModifiedBy = this.puid;
		this.setSampleType();
	}

	checkForMandatoryFieldsForNewRun(): boolean {
		if (!this.selectedProject) {
			this.messagesService.addWarningMessage({
				severity: 'warn', summary: 'Project ID is missing',
				detail: 'Select one of the available projects to proceed further'
			})
			return false
		}
		if (!this.selectedWorkflow) {
			this.messagesService.addWarningMessage({
				severity: 'warn', summary: 'Workflow is missing',
				detail: 'Provide a Workflow to proceed further'
			})
			return false
		}
		if (!this.selectedRun.line) {
			this.messagesService.addWarningMessage({
				severity: 'warn', summary: 'Missing Line',
				detail: 'Select the line from the given options'
			})
			return false
		}
		if (!this.selectedRun.boxes || this.selectedRun.boxes.length === 0) {
			this.messagesService.addWarningMessage({
				severity: 'warn', summary: 'Missing boxes',
				detail: 'Provide atleast 1 box ID to proceed further'
			})
			return false
		}
		return true
	}

	fetchRun(runId) {
		this.searchForRunAction(runId);
	}

	searchForRunAction(runId: string) {
		this.blocked = true;
		this.machineNewService.fetchRun(runId, this.selectedLabId).subscribe({
			next: (result) => {
				this.blocked = false;
				this.selectedRun = result.data;
				this.selectedRun.boxes = this.selectedRun.boxes ? this.selectedRun.boxes : [];
				this.sortPlates();
				this.selectedProject = this.projectData.find(project => project.projectId === this.selectedRun.projectId)
				this.getWorkflowsForProject();
				this.selectedWorkflow = this.projectWorkflowMap.get(`${this.selectedRun.projectId}_${this.selectedRun.workFlow}`);
				this.plateTableSidebarVisible = false;
			}, error: (error) => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity: 'error', summary: 'Failed to fetch run info',
					detail: error.error
				})
			}
		})
	}

	showRunTableSidebarVisible() {
		this.plateTableSidebarVisible = true;
	}

	sortPlates() {
		this.selectedRun.plates.sort((a, b) => {
			const hasA = a.endsWith("_D");
			const hasB = b.endsWith("_D");

			if (hasA && !hasB) {
				return -1;
			} else if (!hasA && hasB) {
				return 1;
			} else {
				return a.localeCompare(b);
			}
		})
	}

	classForDuplicatePlate(plateID: string) {
		return plateID.endsWith('_D') ? "red-text" : "";
	}

	clickPlateID(plateID: string) {
		if (plateID.endsWith('_D')) {
			this.duplicatePlatePopup(plateID);
		}
	}

	duplicatePlatePopup(plateID: string) {
		this.duplicatePlateID = plateID;
		this.showDuplicatePlateIdDialog = true;
		this.disableUpdatePlateIDButton = true;
	}

	clearPopUpFields() {
		this.duplicatePlateID = '';
		this.newPlateID = '';
	}

	checkNewPlateIDPresent() {
		if (this.newPlateID === "") {
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "New PlateID empty!"
			})
			return;
		}

		this.blocked = true;
		this.plateProcessingService.checkDuplicatePlateId(this.selectedLabId, this.newPlateID, "SOURCE").subscribe({
			next: result => {
				this.blocked = false;
				if (result["duplicate"]) {
					this.messagesService.addWarningMessage({
						severity: 'warn', summary: "Plate_ID already exists",
						detail: `${this.newPlateID} already exists. Please scan a new plate-id!`
					})
					this.newPlateID = "";
				}
				else {
					this.disableUpdatePlateIDButton = false;
				}
			}, error: error => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity: "error", summary: "Check Failed!",
					detail: error.error
				})
			}
		})
	}

	updateNewPlateIDInPlate() {
		this.blocked = true;
		this.plateProcessingService.updateNewPlateID(this.selectedLabId, this.duplicatePlateID, this.newPlateID).subscribe({
			next: _result => {
				this.blocked = false;
				this.updateNewPlateIDInRunPlates();
				this.messagesService.addSuccessMessage({
					severity: "success", summary: "Plate updated successfully!!",
					detail: "PlateID updated with " + this.newPlateID
				})
				this.showDuplicatePlateIdDialog = false;
			}, error: error => {
				this.blocked = false;
				this.showDuplicatePlateIdDialog = false;
				this.messagesService.addErrorMessage({
					severity: "error", summary: "Update New PlateID Failed!",
					detail: error.error
				})
			}
		})
	}

	updateNewPlateIDInRunPlates() {
		this.blocked = true;

		const body = {
			"labId": this.selectedLabId,
			"runId": this.selectedRun.runId,
			"duplicatePlateId": this.duplicatePlateID,
			"newPlateId": this.newPlateID
		}

		this.machineNewService.updateRunNewPlateID(body).subscribe({
			next: result => {
				this.blocked = false;
				this.selectedRun = result.data;
				this.selectedRun.boxes = this.selectedRun.boxes ? this.selectedRun.boxes : [];
				this.sortPlates();
			}, error: error => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity: "error", summary: "Update New PlateID Failed!",
					detail: error.error
				})
			}
		})
	}

	disableUpdateButtonOnChange() {
		this.disableUpdatePlateIDButton = true;
	}

	getCommentsDialogHeader() {
		return `${this.selectedRun.runId} comments`;
	}

	showCommentbutton() {
		return this.selectedRun.status && this.selectedRun.status !== "";
	}

	showNoScanDMSamples() {
		return this.selectedRun.noScanDM;
	}

	getProcessStepStartPuid(): string {
		return this.selectedRun.processSteps[0].startPUID;
	}

	getProcessStepStartTimestamp(): Date {
		return this.selectedRun.processSteps[0].startTimeStamp
	}

	isValidProcessStep(): boolean {
		return this.selectedRun.processSteps?.length > 0;
	}
}
